import { CalculadoraGrupoParcelasMensais } from '@/domain/simulador/calculadora-grupo-parcelas-mensais'
import { CalculadoraValoresTotaisSacocH } from '@/domain/simulador/calculadora-valores-totais-sacoch'
import { EntradasMapper } from '@/domain/simulador/entradas-mapper'
import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import type { TSimulacaoCalculoRotinaArquivo } from '@/domain/simulador/factories/types/simulacao-calculo'
import { IntermediariasMapper } from '@/domain/simulador/intermediarias-mapper'
import { MensaisMapper } from '@/domain/simulador/mensais-mapper'
import { CalculadoraDeDescontoExtra } from '@/features/simulador/brasil/domain/calculadora-de-desconto-extra'
import { CalculadoraDeFatorDeVendedor } from '@/features/simulador/brasil/domain/calculadora-de-fator-vendedor'
import { Arquivo } from '@/features/simulador/shared/domain/arquivo'
import { CalculadoraDeComissao } from '@/features/simulador/shared/domain/calculadora-de-comissao'
import { CalculadoraDeFator } from '@/features/simulador/shared/domain/calculadora-de-fator'
import { CalculadoraDeMargem } from '@/features/simulador/shared/domain/calculadora-de-margem'
import { CalculadoraDeParcelasBase } from '@/features/simulador/shared/domain/calculadora-de-parcelas'
import { CalculadoraDeValorMedio } from '@/features/simulador/shared/domain/calculadora-de-valor-medio'
import { CalculadoraDeValorPresenteBase } from '@/features/simulador/shared/domain/calculadora-de-valor-presente'
import { ConversorJuros } from '@/features/simulador/shared/domain/conversor-juros'
import { Mascara, type Fator } from '@/features/simulador/shared/domain/mascara'
import { Rotina } from '@/features/simulador/shared/domain/rotina'
import type { IParcela } from '@/features/simulador/shared/types/calculadora-parcela.types'
import type { TMargem } from '@/features/simulador/shared/types/margem.types'
import { resolvePathWithQuery } from '@/utils/object.utils'

type TSimulacaoCalculoRotinaArquivoSacocH = TSimulacaoCalculoRotinaArquivo & {
  dataPrevisaoDeEntregaDoBloco?: string
  prazoSemCorrecao?: number | null
}

export class RotinaArquivoSacocH {
  private readonly calculadoraComissao: CalculadoraDeComissao

  constructor(
    readonly parametros: Omit<TParametrosRotinaArquivo, 'dataSaldo'>,
    private readonly calculadoraValorPresente: CalculadoraDeValorPresenteBase = new CalculadoraDeValorPresenteBase(),
    private readonly calculadoraParcelas: CalculadoraDeParcelasBase = new CalculadoraDeParcelasBase(),
    private readonly calculadoraDeMargem: CalculadoraDeMargem = new CalculadoraDeMargem(),
    private readonly calculadoraDeFator: CalculadoraDeFator = new CalculadoraDeFator(),
    private readonly mascara: Mascara = new Mascara(),
  ) {
    this.calculadoraComissao = new CalculadoraDeComissao(this.parametros.percentualComissaoVendedor)
  }

  calcular = (grupos: TSimulacaoCalculoRotinaArquivoSacocH) => {
    const taxas = this.obterTaxasDeJuros()
    const parcelasIniciais = this.obterParcelas(grupos)
    const aplicaValorSemCorrecao = this.deveAplicarValorSemCorrecaoINCC(grupos)
    const descontoExtra = this.obterDescontoExtra(parcelasIniciais, grupos)

    const parcelasBaseParaValorPresente = this.etapa2CalcularParcelaSemCorrecao(
      parcelasIniciais,
      grupos,
      taxas.incc,
      aplicaValorSemCorrecao,
    )
    const parcelasComValorPresente = this.etapa1CalcularValorPresente(
      parcelasBaseParaValorPresente,
      grupos.dataSimulacao,
      taxas.depoisPontoMedio,
    )

    const valorPresente = this.etapa3ObterPrecoAVistaDaUnidade(parcelasComValorPresente)
    const comissaoVendedor = this.etapa4CalcularComissaoDoVendedor(valorPresente)

    const parcelasComImposto = this.etapa5CalcularImpostoPorParcela(parcelasComValorPresente, aplicaValorSemCorrecao)
    const parcelasSemComissaoEImposto = this.etapa6DescontarComissaoEImpostos(
      parcelasComImposto,
      comissaoVendedor,
      aplicaValorSemCorrecao,
    )

    const parcelasComPontoMedio = this.etapa7CalcularParcelasNoPontoMedio(
      parcelasSemComissaoEImposto,
      taxas.antesPontoMedio,
      taxas.depoisPontoMedio,
    )
    const valorMedioTotal = this.etapa8ObterValorMedioTotal(parcelasComPontoMedio)
    const valorMedioTotalUPF = this.etapa9ConverterValorMedioTotalEmUPF(valorMedioTotal)
    const margemBrutaEmUPF = this.etapa10GerarMargemBruta(valorMedioTotalUPF)
    const margemLiquidaEmUPF = this.etapa11ObterMargemLiquidaEmUPF(margemBrutaEmUPF)
    const margemRealizada = this.etapa12CalcularMargemRealizada(margemLiquidaEmUPF)
    const margens = this.etapa13CalcularFatorAlcancado(margemRealizada)

    const { fatorVendedor, fatorGcom, fatorGR } = this.etapa14SelecionarFatorDoVendedor(
      margens,
      parcelasComPontoMedio,
      grupos,
    )
    const fatorVendedor1Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor1Por3].fator') ?? 0
    const fatorVendedor2Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor2Por3].fator') ?? 0
    const fatorVendedor3Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor3Por3].fator') ?? 0

    const {
      mascaraVendedor: mascaraVendedorAplicada,
      mascaraGcom: mascaraGcomAplicada,
      mascaraGR: mascaraGRAplicada,
    } = this.etapa15AplicarMascaraDoVendedor(descontoExtra?.fatores ?? { fatorVendedor, fatorGcom, fatorGR })

    const valorPresentePontoMedio = this.etapa16CalcularPrecoAVistaNoPontoMedio(
      valorPresente,
      grupos.dataSimulacao,
      taxas.depoisPontoMedio,
    )
    const valorPresentePontoMedioUPF = this.etapa17ConverterPrecoAVistaNoPontoMedioParaUPF(valorPresentePontoMedio)
    const percentualMargemVenda = this.etapa18ObterPercentualMargemVenda(margemLiquidaEmUPF, valorPresentePontoMedioUPF)
    const rotina = this.etapa19MontarNumeroDaRotina(mascaraVendedorAplicada, percentualMargemVenda)
    const arquivo = this.etapa20MontarNumeroDaArquivo(
      descontoExtra?.valorPresente ?? valorPresente,
      mascaraGcomAplicada,
      mascaraGRAplicada,
    )

    return {
      arquivo,
      rotina,
      comissaoVendedor: Number(comissaoVendedor.toFixed(2)),
      fatorGcom: Number(fatorGcom.toFixed(3)),
      fatorGR: Number(fatorGR.toFixed(3)),
      fatorVendedorPrazoDePagamento: Number(fatorVendedor.toFixed(3)),
      margemBruta: Number(margemBrutaEmUPF.toFixed(2)),
      margemLiquida: Number(margemLiquidaEmUPF.toFixed(2)),
      percentualMargemVenda: Number(percentualMargemVenda.toFixed(2)),
      valorMedioTotal: Number(valorMedioTotal.toFixed(2)),
      valorMedioTotalUPF: Number(valorMedioTotalUPF.toFixed(2)),
      valorPresente: Number(valorPresente.toFixed(2)),
      valorPresentePontoMedio: Number(valorPresentePontoMedio.toFixed(2)),
      valorPresentePontoMedioUPF: Number(valorPresentePontoMedioUPF.toFixed(2)),
      fatorVendedor1Por3: Number(fatorVendedor1Por3.toFixed(3)),
      fatorVendedor2Por3: Number(fatorVendedor2Por3.toFixed(3)),
      fatorVendedor3Por3: Number(fatorVendedor3Por3.toFixed(3)),
    }
  }

  private etapa1CalcularValorPresente(
    parcelas: IParcela[],
    dataSimulacao: string,
    taxaJurosDiariaDepoisPontoMedio: number,
  ) {
    return this.calculadoraValorPresente.calcularValorPresenteDasParcelas(
      parcelas,
      dataSimulacao,
      taxaJurosDiariaDepoisPontoMedio,
    )
  }

  private etapa2CalcularParcelaSemCorrecao(
    parcelas: IParcela[],
    grupos: TSimulacaoCalculoRotinaArquivoSacocH,
    taxaJurosDiariaIncc: number,
    aplicaValorSemCorrecao: boolean,
  ) {
    if (!aplicaValorSemCorrecao) {
      return parcelas
    }

    return this.calculadoraParcelas.calcularValorSemCorrecaoDasParcelas(
      parcelas,
      grupos.dataSimulacao,
      taxaJurosDiariaIncc,
    )
  }

  private etapa3ObterPrecoAVistaDaUnidade(parcelasComValorPresente: IParcela[]) {
    return this.calculadoraParcelas.somar(parcelasComValorPresente, 'valorPresente')
  }

  private etapa4CalcularComissaoDoVendedor(valorPresente: number) {
    return this.calculadoraComissao.calcularComissaoVendedor(valorPresente)
  }
  private etapa5CalcularImpostoPorParcela(parcelasComValorPresente: IParcela[], aplicaValorSemCorrecao: boolean) {
    const valorReferencia = aplicaValorSemCorrecao ? 'valorSemCorrecao' : 'valor'

    return this.calculadoraParcelas.calcularValorDeImpostoDasParcelas(
      parcelasComValorPresente,
      this.parametros.percentualImpostos,
      this.parametros.percentualISS,
      valorReferencia,
    )
  }

  private etapa6DescontarComissaoEImpostos(
    parcelasComImposto: IParcela[],
    comissaoVendedor: number,
    aplicaValorSemCorrecao: boolean,
  ) {
    const valorReferencia = aplicaValorSemCorrecao ? 'valorSemCorrecao' : 'valor'

    return this.calculadoraParcelas.calcularValorSemImpostoEComissaoDasParcelas(
      parcelasComImposto,
      comissaoVendedor,
      valorReferencia,
    )
  }

  private etapa7CalcularParcelasNoPontoMedio(
    parcelasSemComissaoEImposto: IParcela[],
    taxaJurosDiariaAntesPontoMedio: number,
    taxaJurosDiariaDepoisPontoMedio: number,
  ) {
    return this.calculadoraParcelas.calcularValorDePontoMedioDasParcelas(
      parcelasSemComissaoEImposto,
      taxaJurosDiariaAntesPontoMedio,
      taxaJurosDiariaDepoisPontoMedio,
      this.parametros.dataPontoMedio,
    )
  }

  private etapa8ObterValorMedioTotal(parcelasComPontoMedio: IParcela[]) {
    return this.calculadoraParcelas.somar(parcelasComPontoMedio, 'valorPontoMedio')
  }

  private etapa9ConverterValorMedioTotalEmUPF(valorMedioTotal: number) {
    return CalculadoraDeValorMedio.calcularEmUPF(valorMedioTotal, this.parametros.taxaUpf)
  }

  private etapa10GerarMargemBruta(valorMedioTotalUPF: number) {
    return this.calculadoraDeMargem.calcularMargemBrutaEmUPF(valorMedioTotalUPF, this.parametros.custoEmUPF)
  }

  private etapa11ObterMargemLiquidaEmUPF(margemBrutaEmUPF: number) {
    return this.calculadoraDeMargem.calcularMargemLiquidaEmUPF(
      margemBrutaEmUPF,
      this.parametros.patrimonioAfetacao,
      this.parametros.modoDeLucroDaEmpresa,
      this.parametros.percentualIRPJ,
    )
  }

  private etapa12CalcularMargemRealizada(margemLiquidaEmUPF: number) {
    return this.calculadoraDeMargem.calcularMargemRealizada(margemLiquidaEmUPF, {
      margemGR: this.parametros.margemGR,
      margemGcom: this.parametros.margemGcom,
      margemVendedor1Por3: this.parametros.margemVendedor1Por3,
      margemVendedor2Por3: this.parametros.margemVendedor2Por3,
      margemVendedor3Por3: this.parametros.margemVendedor3Por3,
    })
  }

  private etapa13CalcularFatorAlcancado(margemRealizada: ReturnType<CalculadoraDeMargem['calcularMargemRealizada']>) {
    return this.calculadoraDeFator.calcularFatorAlcancado(margemRealizada)
  }

  private etapa14SelecionarFatorDoVendedor(
    margens: TMargem[],
    parcelasComPontoMedio: IParcela[],
    grupos: TSimulacaoCalculoRotinaArquivoSacocH,
  ) {
    const calculadoraDeValoresTotais = new CalculadoraValoresTotaisSacocH(grupos)
    const porcentagemPoupanca = calculadoraDeValoresTotais.percentualPoupanca(
      parcelasComPontoMedio,
      grupos.dataPrevisaoDeEntregaDoBloco,
    )

    return {
      fatorVendedor: CalculadoraDeFatorDeVendedor.calcular(margens, porcentagemPoupanca, this.parametros) ?? 0,
      fatorGcom: margens.find((m) => m.tipo === 'margemGcom')?.fator ?? 0,
      fatorGR: margens.find((m) => m.tipo === 'margemGR')?.fator ?? 0,
    }
  }

  private etapa16CalcularPrecoAVistaNoPontoMedio(
    valorPresente: number,
    dataSimulacao: string,
    taxaJurosDiariaDepoisPontoMedio: number,
  ) {
    return this.calculadoraValorPresente.calcularValorPresenteNoPontoMedio({
      valorPresente,
      taxaJurosDiarioDepoisPontoMedio: taxaJurosDiariaDepoisPontoMedio,
      dataSimulacao,
      dataPontoMedio: this.parametros.dataPontoMedio,
    })
  }

  private etapa17ConverterPrecoAVistaNoPontoMedioParaUPF(valorPresentePontoMedio: number) {
    return this.calculadoraValorPresente.calcularValorPresenteNoPontoMedioEmUPF(
      valorPresentePontoMedio,
      this.parametros.taxaUpf,
    )
  }

  private etapa18ObterPercentualMargemVenda(margemLiquidaEmUPF: number, valorPresentePontoMedioUPF: number) {
    return this.calculadoraDeMargem.calcularPorcentagemMargemDeVenda(margemLiquidaEmUPF, valorPresentePontoMedioUPF)
  }

  private etapa15AplicarMascaraDoVendedor(fatores: Fator) {
    return this.mascara.aplicarMascaraNosFatores(fatores, this.parametros)
  }

  private etapa19MontarNumeroDaRotina(mascaraVendedor: number, percentualMargemVenda: number) {
    return Rotina.montar({
      ordemMascaraVendedor: this.parametros.ordemMascaraVendedor,
      mascaraVendedor,
      percentualMargemVenda,
    })
  }

  private etapa20MontarNumeroDaArquivo(valorPresente: number, mascaraGcom: number, mascaraGR: number) {
    return Arquivo.montar(this.mascara, {
      ordemMascaraGcom: this.parametros.ordemMascaraGcom,
      ordemMascaraGR: this.parametros.ordemMascaraGR,
      valorPresente,
      mascaraGcom,
      mascaraGR,
    })
  }

  private obterParcelas(grupos: TSimulacaoCalculoRotinaArquivoSacocH) {
    const entradas = EntradasMapper.transform(grupos.entradas ?? [])
    const mensais = MensaisMapper.transform(grupos.mensais ?? [])
    const intermediarias = IntermediariasMapper.transform(grupos.intermediarias ?? [])

    const gruposParcelas = { entradas, mensais, intermediarias }
    const tiposParcelas = ['entradas', 'mensais', 'intermediarias'] as const

    return tiposParcelas.flatMap((tipo) =>
      gruposParcelas[tipo].flatMap((grupo) => grupo.parcelas.map((parcela) => ({ ...parcela, tipo }))),
    )
  }

  private obterDescontoExtra(parcelas: IParcela[], grupos: TSimulacaoCalculoRotinaArquivoSacocH) {
    return CalculadoraDeDescontoExtra.habilitar(this.parametros)?.calcularSacocH(
      parcelas,
      grupos.dataSimulacao,
      grupos.dataPrevisaoDeEntregaDoBloco,
    )
  }

  private obterTaxasDeJuros() {
    return {
      antesPontoMedio: ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
        this.parametros.percentualJurosAnualAntesPontoMedio,
      ),
      depoisPontoMedio: ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
        this.parametros.percentualJurosAnualDepoisPontoMedio,
      ),
      incc: ConversorJuros.converterTaxaDeJurosAnualParaDiaria(this.parametros.percentualINCC ?? 0),
    }
  }

  private deveAplicarValorSemCorrecaoINCC(grupos: TSimulacaoCalculoRotinaArquivoSacocH) {
    const quantidadeMensais = CalculadoraGrupoParcelasMensais.totalParcelas(grupos.mensais ?? [])
    return grupos.prazoSemCorrecao != null && quantidadeMensais <= grupos.prazoSemCorrecao
  }
}
